import React, { useState, useEffect } from "react";
import { Upload, message, Modal } from "antd";
import { useIntl } from "@umijs/max";
import { PlusOutlined } from "@ant-design/icons";
import type { UploadFile, UploadProps } from "antd/es/upload/interface";
import { uploadObject } from "@/services/common";

interface ImageUploadProps {
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  maxCount?: number;
  multiple?: boolean;
  accept?: string;
  listType?: "picture-card" | "picture-circle" | "text" | "picture";
  previewType?: "image" | "video";
  disabled?: boolean;
  tip?: string | React.ReactNode;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  value,
  onChange,
  maxCount = 1,
  multiple = false,
  accept = "image/*",
  listType = "picture-card",
  previewType = "image",
  disabled = false,
  tip,
}) => {
  const intl = useIntl();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");

  useEffect(() => {
    // 转换传入的 value 为数组格式
    const incomingUrls = (
      Array.isArray(value) ? value : value ? [value] : []
    ).filter((url) => !!url);

    // 获取当前 fileList 中的有效 url
    const currentUrls = fileList
      .map((file) => file.url || (file.response && file.response.url))
      .filter((url) => !!url);

    // 检查是否有正在上传或出错的文件，这种情况下不要从 props 同步，以免中断上传过程
    const hasUploadingOrError = fileList.some(
      (file) => file.status === "uploading" || file.status === "error",
    );

    // 如果 url 列表一致，且没有正在进行的操作，则不更新，避免重复渲染导致的抖动
    if (
      !hasUploadingOrError &&
      currentUrls.length === incomingUrls.length &&
      currentUrls.every((url, index) => url === incomingUrls[index])
    ) {
      return;
    }

    if (incomingUrls.length === 0) {
      setFileList([]);
      return;
    }

    const newFileList: UploadFile[] = incomingUrls.map((url, index) => ({
      uid: `-${index}`,
      name: url.substring(url.lastIndexOf("/") + 1),
      status: "done",
      url,
    }));
    setFileList(newFileList);
  }, [value]);

  const handleCancel = () => setPreviewVisible(false);

  const handlePreview = async (file: UploadFile) => {
    setPreviewImage(file.url || (file.thumbUrl as string));
    setPreviewVisible(true);
    setPreviewTitle(
      file.name || file.url!.substring(file.url!.lastIndexOf("/") + 1),
    );
  };

  const customRequest = async (options: any) => {
    const { file, onSuccess, onError } = options;
    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await uploadObject(formData);
      const uploadedUrl = res?.fileUrl || res?.url;
      if (uploadedUrl) {
        onSuccess({ url: uploadedUrl });
      } else {
        onError(
          new Error(intl.formatMessage({ id: "pages.common.upload.error" })),
        );
      }
    } catch (error) {
      onError(error);
    }
  };

  const handleChange: UploadProps["onChange"] = ({ fileList: newFileList }) => {
    // 过滤上传失败的文件，实现自动删除功能
    const filteredFileList = newFileList.filter(
      (file) => file.status !== "error",
    );
    setFileList([...filteredFileList]);

    // 只有当所有文件都上传成功或被删除时才触发 onChange
    const allDone = filteredFileList.every((file) => file.status === "done");
    const hasUploading = filteredFileList.some(
      (file) => file.status === "uploading",
    );

    if (allDone && !hasUploading) {
      const urls = filteredFileList
        .map((file) => {
          if (file.url) return file.url;
          if (file.response && file.response.url) return file.response.url;
          return null;
        })
        .filter((url) => !!url);

      if (maxCount === 1 && !multiple) {
        onChange?.(urls[0] || "");
      } else {
        onChange?.(urls);
      }
    } else if (filteredFileList.length === 0) {
      // 处理全部删除的情况
      onChange?.(maxCount === 1 && !multiple ? "" : []);
    }
  };

  const uploadButton = (
    <button
      style={{
        border: 0,
        background: "none",
        cursor: "pointer",
        color: "inherit",
      }}
      type="button"
    >
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>
        {intl.formatMessage({ id: "pages.common.upload" })}
      </div>
    </button>
  );

  return (
    <div className="image-upload-wrapper">
      <Upload
        accept={accept}
        listType={listType}
        fileList={fileList}
        multiple={multiple}
        maxCount={maxCount}
        disabled={disabled}
        customRequest={customRequest}
        onPreview={handlePreview}
        onChange={handleChange}
        style={{ minHeight: listType === "picture-card" ? 102 : "auto" }}
      >
        {fileList.length >= maxCount ? null : uploadButton}
      </Upload>
      {tip && (
        <div
          style={{ marginTop: 8, color: "rgba(0, 0, 0, 0.45)", fontSize: 12 }}
        >
          {tip}
        </div>
      )}
      <Modal
        open={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={handleCancel}
      >
        {previewType === "video" ? (
          // biome-ignore lint/a11y/useMediaCaption: Uploaded game videos do not provide caption tracks.
          <video
            controls
            src={previewImage}
            style={{ width: "100%", maxHeight: "70vh" }}
          />
        ) : (
          <img alt="preview" style={{ width: "100%" }} src={previewImage} />
        )}
      </Modal>
      <style>{`
        .image-upload-wrapper .ant-upload-list-picture-card-container {
          transition: none !important;
        }
      `}</style>
    </div>
  );
};

export default ImageUpload;
